/**
 * Admin V5 — PTE Reviews: Needs Tutor Assignment
 *
 * Route: /admin/pte-reviews
 *
 * Uses existing admin cookie auth. Reuses the shared exam-review pipeline;
 * only surfaces attempts where the canonical tutor was NOT resolvable at
 * submit time. Admin can:
 *   • see 1 card per student with all their unresolved attempts
 *   • see a suggested tutor if canonical relationships exist
 *   • confirm & assign one or ALL unresolved attempts to a chosen active tutor
 *   • trigger the existing PTE review notification once per attempt
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCcw, UserCheck, AlertCircle, CheckCircle2, GraduationCap,
  ShieldAlert,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });

export default function AdminPTEReviewsPage() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);              // null until first successful fetch
  const [dataError, setDataError] = useState(null);    // 'auth' | 'api' | null
  const [tutors, setTutors] = useState(null);          // null = not-yet-loaded, [] = loaded-empty
  const [tutorsError, setTutorsError] = useState(null);// 'auth' | 'api' | null
  const [chosenByStudent, setChosenByStudent] = useState({});
  const [busy, setBusy] = useState({});
  const [backfill, setBackfill] = useState(null);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [search, setSearch] = useState('');
  const [log, setLog] = useState([]);
  const push = (t) => setLog((l) => [...l.slice(-50), `[${new Date().toLocaleTimeString()}] ${t}`]);

  const runBackfill = async () => {
    if (backfillRunning) return;
    if (!window.confirm(
      'Run Historical PTE Backfill?\n\n'
      + 'This is idempotent and safe to re-run.\n'
      + '• Normalises PTE attempts into the shared review schema\n'
      + '• Auto-links a tutor ONLY where the canonical relationship is unambiguous\n'
      + '• Never overwrites responses, scores, IDs or timestamps\n'
      + '• Anything ambiguous stays in Needs Tutor Assignment for Admin confirmation'
    )) return;
    setBackfillRunning(true);
    push('Running historical PTE backfill…');
    try {
      const r = await ax.post('/api/admin/migrations/pte-review-backfill');
      setBackfill(r.data);
      const t = r.data.totals || {};
      push(`Backfill done · updated=${r.data.updated} resolved=${r.data.resolved_this_run} unresolved_this_run=${r.data.unresolved_this_run}`);
      push(`Live totals · total=${t.total} awaiting=${t.awaiting_review} reviewed=${t.reviewed} auto_scored=${t.auto_scored} unresolved=${t.unresolved_tutor}`);
      toast.success(`Backfill complete — ${r.data.updated} row(s) updated, ${t.unresolved_tutor} still unresolved`);
      await load();
    } catch (e) {
      const kind = classify(e);
      if (kind === 'auth') {
        toast.error('Admin session expired — redirecting to sign-in');
        setTimeout(() => nav('/admin/login', { replace: true, state: { from: '/admin/pte-reviews' } }), 400);
      } else {
        toast.error(e.response?.data?.detail || 'Backfill failed');
        push(`Backfill FAILED: ${e.response?.data?.detail || e.message}`);
      }
    } finally {
      setBackfillRunning(false);
    }
  };

  const classify = (e) => (e?.response?.status === 401 || e?.response?.status === 403) ? 'auth' : 'api';

  const load = useCallback(async () => {
    setLoading(true);
    setDataError(null);
    setTutorsError(null);
    // Run both requests independently so one failure does not mask the other
    const results = await Promise.allSettled([
      ax.get('/api/exam/v2/admin/pte/unresolved'),
      ax.get('/api/exam/v2/admin/pte/active-tutors'),
    ]);
    const [q, t] = results;
    if (q.status === 'fulfilled') {
      setData(q.value.data);
    } else {
      const kind = classify(q.reason);
      setDataError(kind);
      // Any auth failure on an admin page → follow existing Admin V5 auth guard
      if (kind === 'auth') {
        toast.error('Admin session expired — redirecting to sign-in');
        setTimeout(() => nav('/admin/login', { replace: true, state: { from: '/admin/pte-reviews' } }), 400);
        setLoading(false);
        return;
      }
      toast.error(q.reason?.response?.data?.detail || 'Unresolved queue failed to load');
    }
    if (t.status === 'fulfilled') {
      setTutors(t.value.data?.items || []);
    } else {
      setTutorsError(classify(t.reason));
    }
    setLoading(false);
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const assign = async (student, attemptIds, bulk, force = false) => {
    const chosen = chosenByStudent[student.student_email || 'unknown']
      || student.suggested_tutor_email;
    if (!chosen) { toast.error('Choose a tutor first'); return; }
    const key = `${student.student_email || 'unknown'}-${bulk ? 'all' : attemptIds.join('.')}`;
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      // Unknown Student groups have no confirmed student_email; assign REVIEW
      // RESPONSIBILITY only, keyed by attempt_ids, without claiming ownership.
      const body = (bulk && student.student_email)
        ? { student_email: student.student_email, tutor_email: chosen, notify: true, force_persist: force }
        : { attempt_ids: attemptIds, tutor_email: chosen, notify: true, force_persist: force };
      const r = await ax.post('/api/exam/v2/admin/pte/assign-tutor', body);
      const canon = (r.data.canonical || []).find((c) => c.student_email === (student.student_email || '').toLowerCase());
      const persisted = canon?.persisted;
      const conflict = canon?.conflict;
      push(`Assigned ${r.data.updated} attempt(s) for ${student.student_email || 'Unknown Student'} → ${chosen} (${r.data.notified} notification${r.data.notified === 1 ? '' : 's'}) · canonical=${persisted ? 'persisted' : (conflict ? `conflict:${canon.reason}` : (canon?.reason || 'not-applicable'))}`);
      if (conflict && !force) {
        const proceed = window.confirm(
          `Attempts were assigned, but the student ${student.student_email} already has a different canonical tutor (${canon.reason}).\n\n`
          + (canon.existing_user_tutor ? `Existing users.assigned_tutor_email = ${canon.existing_user_tutor}\n` : '')
          + (canon.conflicting_booking_tutor ? `Active booking tutor = ${canon.conflicting_booking_tutor.tutor_email} (${canon.conflicting_booking_tutor.course_title || 'no title'})\n` : '')
          + `\nPersist this admin assignment anyway? (multi-tutor students may have several valid tutors; only click OK if this is definitely the correct primary PTE tutor.)`);
        if (proceed) {
          await assign(student, attemptIds, bulk, true);
          return;
        }
      } else if (persisted) {
        toast.success(`Assigned + canonical link persisted`);
      } else {
        toast.success(`Assigned ${r.data.updated} attempt(s)`);
      }
      await load();
    } catch (e) {
      if (classify(e) === 'auth') {
        toast.error('Admin session expired — redirecting to sign-in');
        setTimeout(() => nav('/admin/login', { replace: true, state: { from: '/admin/pte-reviews' } }), 400);
        return;
      }
      toast.error(e.response?.data?.detail || 'Assign failed');
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const totalAttempts = data?.count_attempts || 0;
  const totalStudents = data?.count_students || 0;
  const tutorsLoaded = Array.isArray(tutors);
  const searchLower = search.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    if (!searchLower) return items;
    return items.filter((s) => {
      if ((s.student_name || '').toLowerCase().includes(searchLower)) return true;
      if ((s.student_email || '').toLowerCase().includes(searchLower)) return true;
      if ((s.canonical_name || '').toLowerCase().includes(searchLower)) return true;
      return (s.attempts || []).some((a) =>
        (a.question_type || '').toLowerCase().includes(searchLower)
        || (a.test_title || '').toLowerCase().includes(searchLower));
    });
  }, [data, searchLower]);
  const filteredAttemptCount = filteredItems.reduce((n, s) => n + (s.attempt_count || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-pte-reviews">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold">Admin V5 · PTE Reviews</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
            Needs Tutor Assignment
            {totalAttempts > 0 && (
              <span className="text-sm bg-amber-500 text-black rounded-full px-3 py-1 font-bold"
                    data-testid="pte-unresolved-count">
                {totalAttempts} attempts · {totalStudents} students
              </span>
            )}
          </h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">
            Historical PTE submissions with no canonical tutor. Assign to a tutor to route them into the tutor's PTE review queue. Admin confirmation is required — nothing is auto-assigned silently.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={load} disabled={loading} data-testid="pte-reviews-refresh"
            className="px-4 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
            Refresh
          </button>
          <button onClick={runBackfill} disabled={backfillRunning}
            data-testid="pte-reviews-run-backfill"
            className="px-4 py-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-black text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50">
            {backfillRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
            Run Historical PTE Backfill
          </button>
          <span className="text-xs text-slate-500 self-center ml-2" data-testid="pte-reviews-tutor-count">
            {tutorsError === 'auth'
              ? 'Tutors unavailable — sign in required'
              : tutorsError === 'api'
                ? 'Could not load tutor list — retry'
                : tutorsLoaded
                  ? `${tutors.length} active tutors available`
                  : 'Loading tutors…'}
          </span>
        </div>

        {backfill && (
          <div className="rounded-2xl bg-slate-900 border border-indigo-800 p-4" data-testid="pte-backfill-result">
            <div className="text-xs uppercase tracking-widest text-indigo-300 font-bold mb-1">Last backfill result</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <Metric label="Attempts processed" value={backfill.totals?.total ?? 0} />
              <Metric label="Rows updated this run" value={backfill.updated ?? 0} tone="indigo" />
              <Metric label="Tutor-resolved (this run)" value={backfill.resolved_this_run ?? 0} tone="emerald" />
              <Metric label="Awaiting Review" value={backfill.totals?.awaiting_review ?? 0} />
              <Metric label="Needs Tutor Assignment" value={backfill.totals?.unresolved_tutor ?? 0} tone="amber" />
            </div>
          </div>
        )}

        {/* Search */}
        {data && (data.items || []).length > 0 && (
          <div className="flex items-center gap-2" data-testid="pte-reviews-search-row">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by student name, email, or task type…"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
              data-testid="pte-reviews-search"
            />
            {searchLower && (
              <span className="text-xs text-slate-400" data-testid="pte-reviews-search-count">
                {filteredItems.length} student{filteredItems.length === 1 ? '' : 's'} · {filteredAttemptCount} attempt{filteredAttemptCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
        )}

        {loading && data === null ? (
          <div className="p-6 text-center text-slate-400" data-testid="pte-reviews-loading">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading unresolved queue…
          </div>
        ) : dataError === 'auth' ? (
          <div className="p-6 rounded-2xl bg-rose-950/40 border border-rose-800 text-rose-100" data-testid="pte-reviews-auth-error">
            <ShieldAlert className="h-6 w-6 inline mr-2 text-rose-300" />
            Unable to load PTE review assignments — Admin authentication required.
            <button onClick={() => nav('/admin/login', { replace: true, state: { from: '/admin/pte-reviews' } })}
              className="ml-3 px-3 py-1.5 rounded bg-rose-500 hover:bg-rose-400 text-black font-bold text-xs">
              Sign in
            </button>
          </div>
        ) : dataError === 'api' ? (
          <div className="p-6 rounded-2xl bg-amber-950/40 border border-amber-800 text-amber-100" data-testid="pte-reviews-api-error">
            <AlertCircle className="h-6 w-6 inline mr-2 text-amber-300" />
            Unable to load PTE review assignments. Please retry.
            <button onClick={load} className="ml-3 px-3 py-1.5 rounded bg-amber-400 hover:bg-amber-300 text-black font-bold text-xs">
              Retry
            </button>
          </div>
        ) : data && (data.items || []).length === 0 ? (
          <div className="p-8 text-center rounded-2xl bg-emerald-950/40 border border-emerald-800" data-testid="pte-reviews-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-400 inline mb-2" />
            <div className="text-emerald-300 font-bold text-lg">All PTE submissions are routed.</div>
            <div className="text-slate-400 text-xs mt-1">No unresolved attempts. New submissions auto-link to canonical tutors at submit time.</div>
          </div>
        ) : searchLower && filteredItems.length === 0 ? (
          <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 text-center text-slate-300" data-testid="pte-reviews-search-no-results">
            No unresolved submissions match "<span className="text-white font-semibold">{search}</span>".
          </div>
        ) : (
          <div className="space-y-3" data-testid="pte-reviews-list">
            {filteredItems.map((s) => (
              <StudentCard key={s.student_email || 'unknown'}
                s={s}
                tutors={tutors || []}
                chosen={chosenByStudent[s.student_email || 'unknown'] || ''}
                onChoose={(v) => setChosenByStudent((m) => ({ ...m, [s.student_email || 'unknown']: v }))}
                onAssignOne={(aid) => assign(s, [aid], false)}
                onAssignAll={() => assign(s, s.attempts.map((a) => a.attempt_id), true)}
                busy={busy}
              />
            ))}
          </div>
        )}

        {log.length > 0 && (
          <div className="rounded-2xl bg-black border border-slate-800 p-4 max-h-56 overflow-auto font-mono text-xs text-slate-300"
               data-testid="pte-reviews-log">
            {log.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
      </div>
    </div>
  );
}


function Metric({ label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-100',
    indigo: 'text-indigo-300',
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
  };
  return (
    <div className="rounded-lg bg-slate-950/60 border border-slate-800 p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`text-2xl font-black ${tones[tone] || tones.slate}`}>{value}</div>
    </div>
  );
}

function StudentCard({ s, tutors, chosen, onChoose, onAssignOne, onAssignAll, busy }) {
  const hasSuggestion = !!s.suggested_tutor_email;
  const identifier = s.student_email || 'unknown';
  const key = `${identifier}-all`;
  const value = chosen || s.suggested_tutor_email || '';
  const suggestedInList = useMemo(
    () => tutors.some((t) => t.email === s.suggested_tutor_email),
    [tutors, s.suggested_tutor_email]);
  const chosenTutorName = useMemo(
    () => (tutors.find((t) => t.email === value)?.name)
              || (value === s.suggested_tutor_email ? s.suggested_tutor_name : value)
              || value,
    [tutors, value, s.suggested_tutor_email, s.suggested_tutor_name]);
  const isUnknown = !s.student_email;
  const confirmAndAssignAll = () => {
    if (!value) return;
    const msg = isUnknown
      ? (`These ${s.attempt_count} historical PTE attempts do not currently have a confirmed student identity.\n`
          + `You are about to assign all ${s.attempt_count} review items to ${chosenTutorName}.\n\n`
          + `This does NOT identify the student or change ownership of the attempts. `
          + `The attempts remain 'Unknown Student' and only review responsibility moves to the tutor.\n\n`
          + `Continue?`)
      : (`Assign all ${s.attempt_count} unresolved PTE submissions for ${s.student_name || s.student_email} to ${chosenTutorName}?`);
    if (window.confirm(msg)) onAssignAll();
  };
  return (
    <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4 space-y-3"
         data-testid={`pte-unresolved-${identifier}`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="text-lg font-bold text-white" data-testid={`pte-unresolved-name-${identifier}`}>
          {s.student_name || s.student_email || 'Unknown Student'}
        </div>
        {s.canonical_name && (
          <span className="text-[10px] uppercase tracking-widest bg-emerald-500/20 border border-emerald-500 text-emerald-200 rounded-full px-2 py-0.5 font-bold">
            Canonical match
          </span>
        )}
        {s.student_email && <div className="text-xs text-slate-400">{s.student_email}</div>}
        <span className="text-xs bg-amber-500/20 border border-amber-500 text-amber-200 rounded-full px-2 py-0.5 font-bold">
          {s.attempt_count} unresolved
        </span>
        {!s.student_email && (
          <span className="text-xs bg-rose-500/20 border border-rose-500 text-rose-200 rounded-full px-2 py-0.5 font-bold inline-flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> No email on attempts
          </span>
        )}
      </div>

      {s.course_booking_ref && (
        <div className="text-xs text-slate-400">
          Latest booking: <span className="text-slate-200 font-semibold">{s.course_booking_ref.course_title || '—'}</span>
          {s.course_booking_ref.tutor_email && (
            <> · Booking tutor: <span className="text-slate-200">{s.course_booking_ref.tutor_name || s.course_booking_ref.tutor_email}</span></>
          )}
        </div>
      )}

      {/* Attempts list */}
      <div className="rounded-lg border border-slate-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/50 text-slate-400 uppercase tracking-wider text-[10px]">
            <tr><th className="p-2 text-left">Task</th><th className="p-2 text-left">Submitted</th><th className="p-2 text-left">Status</th><th className="p-2 text-right">Action</th></tr>
          </thead>
          <tbody>
            {s.attempts.map((a) => (
              <tr key={a.attempt_id} className="border-t border-slate-800"
                  data-testid={`pte-unresolved-row-${a.attempt_id}`}>
                <td className="p-2">
                  <div className="text-slate-100 font-semibold">{a.test_title || a.question_type}</div>
                  <div className="text-[10px] text-slate-500">{a.test_type}</div>
                </td>
                <td className="p-2 text-slate-400">{a.submitted_at ? new Date(a.submitted_at).toLocaleString() : '—'}</td>
                <td className="p-2">
                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-200 border border-amber-500">
                    {a.review_status || 'pending'}
                  </span>
                </td>
                <td className="p-2 text-right">
                  <button
                    disabled={!value || busy[`${identifier}-${a.attempt_id}`]}
                    onClick={() => onAssignOne(a.attempt_id)}
                    data-testid={`pte-unresolved-assign-${a.attempt_id}`}
                    className="text-[11px] px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 text-black font-bold disabled:opacity-40">
                    Assign
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Choose tutor */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs text-slate-400 font-semibold">Tutor:</div>
        <select
          value={value}
          onChange={(e) => onChoose(e.target.value)}
          data-testid={`pte-unresolved-select-${identifier}`}
          className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 min-w-[220px]">
          <option value="">Select tutor…</option>
          {hasSuggestion && (
            <option value={s.suggested_tutor_email} data-testid={`pte-unresolved-suggest-${identifier}`}>
              Suggested: {s.suggested_tutor_name || s.suggested_tutor_email} ({s.suggested_tutor_source})
            </option>
          )}
          {tutors
            .filter((t) => t.email !== s.suggested_tutor_email)
            .map((t) => (
              <option key={t.email} value={t.email}>{t.name} · {t.email}</option>
            ))}
        </select>
        {hasSuggestion && !suggestedInList && (
          <span className="text-[10px] text-amber-300">(suggested is not in active tutor list — you may still assign)</span>
        )}
        <button
          disabled={!value || busy[key]}
          onClick={confirmAndAssignAll}
          data-testid={`pte-unresolved-assign-all-${identifier}`}
          className={`ml-auto text-xs px-3 py-1.5 rounded-full text-white font-bold inline-flex items-center gap-1 disabled:opacity-40 ${
            isUnknown ? 'bg-rose-600 hover:bg-rose-500' : 'bg-indigo-500 hover:bg-indigo-400'
          }`}>
          <UserCheck className="h-3.5 w-3.5" />
          {isUnknown
            ? `Assign ${s.attempt_count} review items to this tutor`
            : `Assign all ${s.attempt_count} to this tutor`}
        </button>
      </div>
    </div>
  );
}
